"use client";

import React, { useState, useRef, useEffect, useCallback, ReactNode } from "react";
import { createPortal } from "react-dom";

type Position = "top" | "right" | "bottom" | "left";

interface HoverPopoverProps {
  position: Position;
  trigger: React.ReactNode;
  children: ReactNode;
  delay?: number; // Delay in ms before showing/hiding
  className?: string; // Allow custom wrapper classes
}

export default function HoverPopover({ 
  position, 
  trigger, 
  children, 
  delay = 200,
  className = "relative inline-block"
}: HoverPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isHoveringTrigger, setIsHoveringTrigger] = useState(false);
  const [isHoveringPopover, setIsHoveringPopover] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  // Calculate popover position based on trigger element
  const calculatePopoverPosition = useCallback(() => {
    if (!triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const popoverWidth = 280; // Match the w-[280px] class
    // Use actual height if available, otherwise estimate based on typical content (120px is more accurate for calendar popovers)
    const popoverHeight = popoverRef.current?.offsetHeight || 120;
    const gap = 4; // Smaller spacing between trigger and popover for tighter fit

    let top = 0;
    let left = 0;

    switch (position) {
      case "top":
        // Position directly above the trigger with minimal gap
        top = triggerRect.top - popoverHeight - gap;
        left = triggerRect.left + triggerRect.width / 2 - popoverWidth / 2;
        break;
      case "bottom":
        top = triggerRect.bottom + gap;
        left = triggerRect.left + triggerRect.width / 2 - popoverWidth / 2;
        break;
      case "left":
        top = triggerRect.top + triggerRect.height / 2 - popoverHeight / 2;
        left = triggerRect.left - popoverWidth - gap;
        break;
      case "right":
        top = triggerRect.top + triggerRect.height / 2 - popoverHeight / 2;
        left = triggerRect.right + gap;
        break;
    }

    // Ensure popover stays within viewport bounds
    const padding = 10;
    const maxLeft = window.innerWidth - popoverWidth - padding;
    const maxTop = window.innerHeight - popoverHeight - padding;

    left = Math.max(padding, Math.min(left, maxLeft));
    top = Math.max(padding, Math.min(top, maxTop));

    setPopoverPosition({ top, left });
  }, [position]);

  // Show popover when hovering trigger or popover
  useEffect(() => {
    if (isHoveringTrigger || isHoveringPopover) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setIsOpen(true);
      // Calculate position immediately
      calculatePopoverPosition();
      // Recalculate after a brief delay to get actual rendered height
      setTimeout(() => calculatePopoverPosition(), 10);
    } else {
      timeoutRef.current = setTimeout(() => {
        setIsOpen(false);
      }, delay);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isHoveringTrigger, isHoveringPopover, delay, calculatePopoverPosition]);

  // Recalculate position on scroll or resize
  useEffect(() => {
    if (isOpen) {
      window.addEventListener('scroll', calculatePopoverPosition, true);
      window.addEventListener('resize', calculatePopoverPosition);
      
      return () => {
        window.removeEventListener('scroll', calculatePopoverPosition, true);
        window.removeEventListener('resize', calculatePopoverPosition);
      };
    }
  }, [isOpen, calculatePopoverPosition]);

  const handleTriggerMouseEnter = () => {
    setIsHoveringTrigger(true);
  };

  const handleTriggerMouseLeave = () => {
    setIsHoveringTrigger(false);
  };

  const handlePopoverMouseEnter = () => {
    setIsHoveringPopover(true);
  };

  const handlePopoverMouseLeave = () => {
    setIsHoveringPopover(false);
  };

  const arrowClasses = {
    top: "bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45",
    right: "left-0 top-1/2 transform -translate-y-1/2 -translate-x-1/2 rotate-45",
    bottom: "top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-45",
    left: "right-0 top-1/2 transform -translate-y-1/2 translate-x-1/2 rotate-45",
  };

  const arrowBorderClasses = {
    top: "border-r border-b border-gray-200 dark:border-gray-600",
    right: "border-l border-b border-gray-200 dark:border-gray-600", 
    bottom: "border-l border-t border-gray-200 dark:border-gray-600",
    left: "border-r border-t border-gray-200 dark:border-gray-600",
  };

  // Create popover portal element
  const popoverPortal = isOpen && typeof window !== 'undefined' ? createPortal(
    <div
      ref={popoverRef}
      onMouseEnter={handlePopoverMouseEnter}
      onMouseLeave={handlePopoverMouseLeave}
      className="fixed w-[280px] z-[99999]"
      style={{
        top: `${popoverPosition.top}px`,
        left: `${popoverPosition.left}px`,
        pointerEvents: 'auto',
      }}
    >
      <div className="w-full bg-white rounded-lg shadow-lg border border-gray-200 dark:bg-[#1E2634] dark:border-gray-600">
        {children}
        <div
          className={`absolute w-3 h-3 bg-white dark:bg-[#1E2634] ${arrowBorderClasses[position]} ${arrowClasses[position]}`}
        ></div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <div className={className}>
        <div 
          ref={triggerRef} 
          onMouseEnter={handleTriggerMouseEnter}
          onMouseLeave={handleTriggerMouseLeave}
        >
          {trigger}
        </div>
      </div>
      {popoverPortal}
    </>
  );
} 